import { inject, Injectable } from '@angular/core';
import { config } from '../../../config';
import { ProviderName } from '../../../../account/models/login-response.model';
import { LoggingService } from '../../core/logging.service';
import { ExpressCheckoutService } from './express-checkout.service';
import {
  ExpressCheckoutContext,
  ExpressCheckoutHandlers,
  ExpressCheckoutMode,
} from '../../../models/orders/express-checkout.token';
import {
  PazeCanCheckoutRequest,
  PazeCheckoutRequest,
  PazeCheckoutResponse,
  PazeCompleteRequest,
  PazeCompleteResponse,
  PazeError,
  PazeInitializeRequest,
  PazeSdk,
  PazeTransactionValue,
} from '../../../models/paze/paze.model';

/**
 * Separates the two JWS tokens sent to the backend as a single responseString.
 *
 * Safe as a delimiter because a JWS is base64url, whose alphabet is A-Z a-z 0-9 - _ plus '.' as
 * the segment separator - so a run of hyphens this long cannot occur inside either token.
 */
const PAZE_TOKEN_SEPARATOR = '--------';

/**
 * Thin TypeScript wrapper around the Paze Digital Wallet SDK (window.DIGITAL_WALLET_SDK) driving the
 * Auto-Initiate with Express Pay flow. All SDK calls fail gracefully so they never block the native
 * checkout. Merchant credentials and SDK urls are resolved from config.providerSettings.
 */
@Injectable({
  providedIn: 'root',
})
export class PazeService {
  private logService = inject(LoggingService);
  private expressCheckoutService = inject(ExpressCheckoutService);

  private initialized = false;

  /* ================================
   * Public API
   * ================================ */

  /**
   * Load the Paze SDK script and call initialize() once. Idempotent and safe to call on every
   * checkout render. Returns false (without throwing) if Paze is unconfigured or initialize fails,
   * so callers can silently fall back to the native flow.
   */
  async initialize(options: { settings: PazeInitializeRequest; scripts: string[] }): Promise<boolean> {
    if (this.initialized) {
      return true;
    }

    const { settings, scripts } = options;
    if (!settings?.client?.id || !settings?.client?.profileId) {
      this.logService.info('Paze is not configured, skipping initialize');
      return false;
    }

    try {
      await Promise.all(scripts.map((script) => this.expressCheckoutService.loadScript(script)));
      const sdk = this.getSdk();
      if (!sdk) {
        this.logService.error('Paze SDK not available after loading script');
        return false;
      }

      await sdk.initialize(settings);
      this.initialized = true;
      return true;
    } catch (error) {
      this.logService.error('Paze initialize failed:', this.toPazeError(error));
      return false;
    }
  }

  /**
   * Ask Paze whether this consumer has a wallet, so the button is only offered to someone who can
   * actually pay with it. Identified by email address or mobile number — at least one is required,
   * so with neither the answer is false without calling the SDK.
   *
   * Fails closed: an unavailable SDK, a rejected lookup or a malformed response all resolve to
   * false, hiding the button rather than offering a wallet that would dead-end on press.
   */
  async canCheckout(params: { emailAddress?: string; mobileNumber?: string; sessionId?: string }): Promise<boolean> {
    if (!params.emailAddress && !params.mobileNumber) {
      this.logService.info('Paze: no email address or mobile number to check eligibility with, hiding express checkout');
      return false;
    }

    const sdk = this.getSdk();
    if (!sdk) {
      return false;
    }

    const request: PazeCanCheckoutRequest = {
      emailAddress: params.emailAddress,
      mobileNumber: params.mobileNumber,
      sessionId: params.sessionId,
    };

    try {
      const response = await sdk.canCheckout(request);
      return !!response?.consumerPresent;
    } catch (error) {
      this.logService.error('Paze canCheckout failed:', this.toPazeError(error));
      return false;
    }
  }

  /**
   * Run the full Paze express flow for a context: checkout -> complete -> submit, reporting the
   * outcome via the handlers. Called by the base component's submit() (wallet button press).
   */
  async submit(context: ExpressCheckoutContext, handlers: ExpressCheckoutHandlers): Promise<void> {
    const sessionId = this.createSessionId();
    const transactionValue = this.buildTransactionValue(context);

    try {
      const checkout = await this.checkout({
        emailAddress: context.emailAddress,
        mobileNumber: context.mobileNumber,
        sessionId,
        transactionValue,
        mode: context.mode,
      });

      if (checkout.result !== 'COMPLETE' || !checkout.checkoutResponse) {
        // Ineligible / cancelled — restore the native flow.
        this.logService.info('Paze: customer ineligible or cancelled (INCOMPLETE) — falling back to native checkout');
        handlers.onIncomplete({ providerId: ProviderName.Paze, success: false });
        return;
      }

      const complete = await this.complete({ sessionId, transactionValue, mode: context.mode });

      // Both tokens go to the server as one string: the checkout JWS carries the
      // consumer/session assertion and the complete JWS the secured payment payload,
      // and the server needs the pair to decrypt and charge. Split on the separator.
      const responseString = `${complete.completeResponse}${PAZE_TOKEN_SEPARATOR}${checkout.checkoutResponse}`;
      const submitted = await this.submitToBackend(responseString, context);

      handlers.onResult({ providerId: ProviderName.Paze, success: submitted, data: context.orderId });
    } catch (error: any) {
      this.logService.error('Paze express checkout failed:', error);
      handlers.onResult({
        providerId: ProviderName.Paze,
        success: false,
        error: { reason: error?.reason, message: error?.message },
      });
    }
  }

  /** Build the Paze transaction value from a checkout context (amount is minor units -> major decimal). */
  private buildTransactionValue(context: ExpressCheckoutContext): PazeTransactionValue {
    return {
      transactionCurrencyCode: context.currencyCode,
      transactionAmount: (context.amount / 100).toFixed(2),
    };
  }

  /**
   * Launch the Paze wallet for an eligible customer. Returns the checkout response, or an
   * INCOMPLETE result if the SDK is unavailable / the customer is ineligible / an error occurs.
   */
  async checkout(params: {
    emailAddress?: string;
    mobileNumber?: string;
    sessionId: string;
    transactionValue: PazeTransactionValue;
    /** 'card-token' saves a card (ADD_CARD intent); anything else runs the Express Pay purchase flow. */
    mode?: ExpressCheckoutMode;
  }): Promise<PazeCheckoutResponse> {
    const sdk = this.getSdk();
    if (!sdk) {
      return { result: 'INCOMPLETE' };
    }

    const isAddCard = params.mode === 'card-token';
    const request: PazeCheckoutRequest = {
      emailAddress: params.emailAddress,
      mobileNumber: params.mobileNumber,
      sessionId: params.sessionId,
      actionCode: 'START_FLOW',
      // ADD_CARD tokenises a card: it takes NO transactionValue and requires shippingPreference NONE.
      intent: isAddCard ? 'ADD_CARD' : 'EXPRESS_CHECKOUT',
      confirmLaunch: false,
      ...(isAddCard ? { shippingPreference: 'NONE' as const } : { transactionValue: params.transactionValue }),
    };
    try {
      const response = await sdk.checkout(request);
      return response;
    } catch (error) {
      this.logService.error('Paze checkout failed:', this.toPazeError(error));
      return { result: 'INCOMPLETE' };
    }
  }

  /**
   * Finalize the transaction and return the encrypted payment payload (JWS) for the processor.
   * Throws the mapped PazeError on failure so the caller can trigger a retry / fallback.
   */
  async complete(params: {
    sessionId: string;
    transactionValue: PazeTransactionValue;
    /** 'card-token' saves a card (CARD_ON_FILE, ID payload); anything else finalises a PURCHASE. */
    mode?: ExpressCheckoutMode;
  }): Promise<PazeCompleteResponse> {
    const sdk = this.getSdk();
    if (!sdk) {
      throw { reason: 'SDK_UNAVAILABLE', message: 'Paze SDK not loaded' } as PazeError;
    }

    const isCardOnFile = params.mode === 'card-token';
    const request: PazeCompleteRequest = {
      transactionType: isCardOnFile ? 'CARD_ON_FILE' : 'PURCHASE',
      sessionId: params.sessionId,
      // transactionValue is only required for PURCHASE/BOTH; omit it for a CARD_ON_FILE save.
      ...(isCardOnFile ? {} : { transactionValue: params.transactionValue }),
      transactionOptions: {
        // A card save returns only an identifier (ID); a purchase returns the payment JWE.
        payloadTypeIndicator: isCardOnFile ? 'PAYMENT' : 'PAYMENT',
        billingPreference: 'ALL',
      },
      pazeOnFile: true,
    };

    try {
      return await sdk.complete(request);
    } catch (error) {
      throw this.toPazeError(error);
    }
  }

  /**
   * Complete the express checkout with the encrypted token payload for server-side JWE decryption
   * and charging. Routes to the card-token or marketplace complete endpoint by `context.mode` via
   * `completeExpress`. Resolves to true on a successful response.
   *
   * `completeResponse` is the checkout and complete JWS tokens joined by PAZE_TOKEN_SEPARATOR, so
   * the server must split on it before verifying either one.
   */
  async submitToBackend(completeResponse: string, context: ExpressCheckoutContext): Promise<boolean> {
    try {
      const response: any = await this.expressCheckoutService.completeExpress(context, ProviderName.Paze, {
        responseString: completeResponse,
      });
      return !!(response?.Success ?? response?.success);
    } catch (error) {
      this.logService.error('Paze submitToBackend failed:', error);
      return false;
    }
  }

  /** Generate a fresh session id for a checkout. A new session is required per checkout / on timeout. */
  createSessionId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    // Fallback for environments without crypto.randomUUID.
    return `paze-${Math.abs(this.hashString(`${config?.portalUrl}-${performance.now()}`))}`;
  }

  /* ================================
   * Private Helpers
   * ================================ */

  private getSdk(): PazeSdk | null {
    return ((window as any).DIGITAL_WALLET_SDK as PazeSdk) || null;
  }

  private toPazeError(error: unknown): PazeError {
    if (error && typeof error === 'object' && ('reason' in error || 'message' in error)) {
      return error as PazeError;
    }
    return { message: String(error) };
  }

  private hashString(value: string): number {
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
      hash = (hash << 5) - hash + value.charCodeAt(i);
      hash |= 0;
    }
    return hash;
  }
}
